import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import React from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100 , 150 , 200 , 300];

export default function Pagination({
  currentPage,
  totalPages,
  perPage,
  totalRecords,
  onPageChange,
  onPerPageChange,
  className = "",
  title=""
}) {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    if (currentPage > 3) pages.push(-1); // ellipsis

    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) end = Math.min(totalPages - 1, 4);
    if (currentPage >= totalPages - 2) start = Math.max(2, totalPages - 3);

    for (let i = start; i <= end; i++) pages.push(i);

    if (currentPage < totalPages - 2) pages.push(-2); // ellipsis

    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {/* Showing text */}
      <div className="text-sm text-muted-foreground">
        Showing {(currentPage - 1) * perPage + 1} to{" "}
        {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} {title}
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* Rows per page - shadcn Select */}
        <div className="flex items-center gap-2 text-sm">
          <span className="whitespace-nowrap">Rows per page</span>
          <Select
            value={String(perPage)}           // important: string mein convert
            onValueChange={(value) => onPerPageChange(value)}
          >
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue placeholder={perPage} />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pagination buttons */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {pages.map((page, idx) => (
            <React.Fragment key={idx}>
              {page > 0 ? (
                <Button
                  variant={page === currentPage ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </Button>
              ) : (
                <span className="flex h-8 w-8 items-center justify-center text-muted-foreground">
                  ...
                </span>
              )}
            </React.Fragment>
          ))}

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}